'use client';

import Image from 'next/image';
import { useMemo, useRef } from 'react';
import { useProductInteraction } from '@/app/(users)/totem-by-slug/[slug]/hooks/use-product-interaction';
import { useTotem } from '@/app/(users)/totem-by-slug/[slug]/providers/totem-provider';
import type { MenuSnapshotProduct } from '@/app/(users)/totem-by-slug/[slug]/types/menu-snapshot';
import { getProductsByCategory } from '@/app/(users)/totem-by-slug/[slug]/utils/category-products';
import { getProductDisplayName } from '@/app/(users)/totem-by-slug/[slug]/utils/product-name';
import { formatTotemPrice } from '@/app/(users)/totem-by-slug/[slug]/utils/totem-price';
import {
  getProductStockLimit,
  isProductOutOfStock,
  shouldShowProduct,
} from "@/app/(users)/totem-by-slug/[slug]/utils/product-stock";
import { ScrollArea } from '@/components/ui/scroll-area';
import { getPlaceholderImageUrl } from '@/lib/images/image-presets';
import { cn } from '@/lib/utils';

type ProductCardProps = {
  product: MenuSnapshotProduct;
  onSelect?: (product: MenuSnapshotProduct, rect: DOMRect) => void;
  compact?: boolean;
  readOnly?: boolean;
};

function TotemProductCard({
  product,
  onSelect,
  compact,
  readOnly = false,
}: ProductCardProps) {
  const ref = useRef<HTMLButtonElement>(null);
  const name = getProductDisplayName(product);
  const { cart } = useTotem();

  const stockLimit = useMemo(
    () => getProductStockLimit(product.product_stock),
    [product.product_stock],
  );
  const isOutOfStock = stockLimit <= 0;
  const totalInCart = useMemo(() => {
    return cart.items
      .filter((i) => i.productId === product.id)
      .reduce((sum, i) => sum + i.quantity, 0);
  }, [cart.items, product.id]);

  const isAtLimit = totalInCart >= stockLimit;
  const isDisabled = isOutOfStock || isAtLimit;

  const priceFrom = useMemo(() => {
    if (Number(product.price) > 0) return 0;
    let min = 0;
    for (const ps of product.product_subselection ?? []) {
      const sub = ps.subselection;
      if (!sub) continue;
      for (const item of sub.subselection_item ?? []) {
        const isOutOfStockItem = isProductOutOfStock(item.product_stock);
        if (
          !isOutOfStockItem &&
          item.price > 0 &&
          (min === 0 || item.price < min)
        ) {
          min = item.price;
        }
      }
    }
    return min;
  }, [product]);

  if (readOnly) {
    return (
      <div
        className={cn(
          "flex flex-col items-center text-center rounded-xl select-none cursor-default",
          compact ? "gap-1 p-1 sm:gap-1.5 sm:p-1 lg:gap-3 lg:p-2" : "gap-3 p-2",
        )}
      >
        <div
          className={cn(
            "relative aspect-square w-full overflow-hidden rounded-xl bg-white dark:bg-muted/30",
            compact
              ? "max-w-24 sm:max-w-28 lg:max-w-32 xl:max-w-48"
              : "max-w-48",
          )}
        >
          <Image
            alt={name}
            className={cn(
              "object-contain p-2",
              (!product.imageurl || isOutOfStock) && "opacity-60 grayscale",
            )}
            fill
            sizes="(max-width: 768px) 33vw, 200px"
            src={product.imageurl || getPlaceholderImageUrl("product")}
          />
          {isOutOfStock && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
              <span className="bg-red-600 text-white text-[11px] sm:text-xs font-bold px-2 py-0.5 rounded-md shadow">
                Sin stock
              </span>
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1">
          <p
            className={cn(
              "font-medium capitalize",
              compact
                ? "text-[11px] leading-tight sm:text-xs lg:text-sm"
                : "text-sm",
            )}
          >
            {name}
          </p>
          <p
            className={cn(
              "font-bold text-amber-600",
              compact ? "text-xs sm:text-sm lg:text-base" : "text-base",
            )}
          >
            {Number(product.price) === 0 && priceFrom > 0
              ? `Desde ${formatTotemPrice(priceFrom)}`
              : formatTotemPrice(Number(product.price))}
          </p>
        </div>
      </div>
    );
  }

  return (
    <button
      ref={ref}
      type="button"
      disabled={isDisabled}
      className={cn(
        "flex flex-col items-center text-center transition-opacity rounded-xl relative",
        isDisabled
          ? "opacity-60 cursor-not-allowed"
          : "hover:opacity-80 cursor-pointer",
        compact ? "gap-1 p-1 sm:gap-1.5 sm:p-1 lg:gap-3 lg:p-2" : "gap-3 p-2",
      )}
      onClick={() => {
        if (isDisabled) return;
        const rect = ref.current?.getBoundingClientRect();
        if (rect) onSelect?.(product, rect);
      }}
    >
      <div
        className={cn(
          "relative aspect-square w-full overflow-hidden rounded-xl bg-white dark:bg-muted/30",
          compact ? "max-w-24 sm:max-w-28 lg:max-w-32 xl:max-w-48" : "max-w-48",
        )}
      >
        <Image
          alt={name}
          className={cn(
            "object-contain p-2",
            (!product.imageurl || isOutOfStock) && "opacity-60 grayscale",
          )}
          fill
          sizes="(max-width: 768px) 33vw, 200px"
          src={product.imageurl || getPlaceholderImageUrl("product")}
        />
        {isOutOfStock && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
            <span className="bg-red-600 text-white text-[11px] sm:text-xs font-bold px-2 py-0.5 rounded-md shadow">
              Sin stock
            </span>
          </div>
        )}
        {!isOutOfStock && isAtLimit && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
            <span className="bg-amber-600 text-white text-[11px] sm:text-xs font-bold px-2 py-0.5 rounded-md shadow">
              Límite alcanzado
            </span>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-1">
        <p
          className={cn(
            "font-medium capitalize",
            compact
              ? "text-[11px] leading-tight sm:text-xs lg:text-sm"
              : "text-sm",
          )}
        >
          {name}
        </p>
        <p
          className={cn(
            "font-bold text-amber-600",
            compact ? "text-xs sm:text-sm lg:text-base" : "text-base",
          )}
        >
          {Number(product.price) === 0 && priceFrom > 0
            ? `Desde ${formatTotemPrice(priceFrom)}`
            : formatTotemPrice(Number(product.price))}
        </p>
      </div>
    </button>
  );
}

interface TotemProductGridProps {
  readOnly?: boolean;
}

export function TotemProductGrid({ readOnly = false }: TotemProductGridProps) {
  const compact = true;

  const { activeCategoryName, activeCategoryId, menu, searchQuery } =
    useTotem();
  const { handleProductClick } = useProductInteraction();

  const activeSearch = searchQuery.trim().toLowerCase();

  const products = useMemo(() => {
    if (!menu?.products) return [];

    if (activeSearch) {
      return menu.products
        .filter(product => {
          if (!shouldShowProduct(product)) return false;
          return getProductDisplayName(product).toLowerCase().includes(activeSearch);
        })
        .sort((a, b) => getProductDisplayName(a).localeCompare(getProductDisplayName(b), 'es'));
    }

    if (!activeCategoryId) return [];

    return getProductsByCategory(menu.products, activeCategoryId);
  }, [menu?.products, activeSearch, activeCategoryId]);

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white dark:bg-transparent">
      <div
        className={cn(
          "shrink-0 px-6 pt-6 items-center min-h-16 gap-4",
          compact ? "flex flex-col sm:grid sm:grid-cols-3" : "grid grid-cols-3",
        )}
      >
        <h2
          className={cn(
            "font-bold truncate w-full text-left",
            compact ? "text-lg sm:text-2xl" : "text-2xl",
          )}
        >
          {activeSearch
            ? `Resultados para "${searchQuery}" (${products.length})`
            : activeCategoryName}
        </h2>

        {/* Empty div for right column balance */}
        <div />
      </div>
      <ScrollArea className="min-h-0 flex-1 px-6 pb-6 pt-4">
        {products.length === 0 ? (
          <p className="text-muted-foreground">
            No hay productos en esta categoría.
          </p>
        ) : (
          <div
            className={cn(
              "grid",
              compact
                ? "grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-5"
                : "grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3",
            )}
          >
            {products.map((product) => (
              <TotemProductCard
                key={product.id}
                product={product}
                compact={compact}
                readOnly={readOnly}
                onSelect={
                  readOnly
                    ? undefined
                    : (p, rect) => handleProductClick(p, rect)
                }
              />
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}
